import { Info, Mail, Trash2 } from 'lucide-react';
import { Control, FieldArrayWithId } from 'react-hook-form';

import { type TeamMember, InviteFormValues } from '../types/team-types';
import { capitalizeRole, getAvailableRoles } from '../helpers/team-member-helpers';

import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '@/components/ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/ui/input';
import { SELECT_APPEARANCE } from '@/components/ui/types/select-variants-enums';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface InvitationRowProps {
  field: FieldArrayWithId<InviteFormValues, 'invitations', 'id'>;
  index: number;
  control: Control<InviteFormValues>;
  onRemove: (index: number) => void;
  canRemove: boolean;
  currentUserRole: TeamMember['role'] | null;
  showLabels?: boolean;
}

export default function InvitationRow({
  field: _field,
  index,
  control,
  onRemove,
  canRemove,
  currentUserRole,
  showLabels = true,
}: InvitationRowProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(8rem,1fr)_2.25rem] sm:items-start">
      <FormField
        control={control}
        name={`invitations.${index}.email`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className={showLabels ? undefined : 'sr-only'}>Email</FormLabel>
            <FormControl>
              <Input
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                leftIcon={<Mail className="size-4" />}
                size={CONTROL_SIZE.COMPACT}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`invitations.${index}.role`}
        render={({ field }) => (
          <FormItem>
            {showLabels ? (
              <div className="flex items-center gap-1">
                <FormLabel>Role</FormLabel>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="About team roles"
                      className="text-content-tertiary"
                    >
                      <Info className="size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Choose this member&apos;s access level.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <FormLabel className="sr-only">Role</FormLabel>
            )}
            <Select
              appearance={SELECT_APPEARANCE.DEFAULT}
              size={CONTROL_SIZE.COMPACT}
              onValueChange={field.onChange}
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {getAvailableRoles(currentUserRole).map(role => (
                  <SelectItem key={role} value={role}>
                    {capitalizeRole(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <div
        className={`flex justify-end sm:justify-start ${showLabels ? 'sm:pt-7' : ''} ${
          canRemove ? '' : 'invisible'
        }`}
      >
        <IconButton
          label="Remove invitation row"
          size={ICON_BUTTON_SIZE.COMPACT}
          variant={ICON_BUTTON_VARIANT.DESTRUCTIVE}
          onClick={() => onRemove(index)}
          disabled={!canRemove}
        >
          <Trash2 aria-hidden="true" />
        </IconButton>
      </div>
    </div>
  );
}
